import React, { useState, useEffect } from 'react';
import { getVendors, getVendorContacts, createVendor, updateVendor, deleteVendor, createVendorContact, deleteVendorContact, exportVendors } from '../../lib/api';
import { downloadBlob } from '../../lib/downloadHelper';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Loader2,
  AlertCircle,
  Phone,
  Mail,
  MapPin,
  Users,
  Search,
  Download,
  Printer
} from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useAuth } from '../../context/AuthContext';

const Vendors = () => {
  const { isAdmin } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Vendor dialog state
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorFormData, setVendorFormData] = useState({
    vendor_name: '',
    phone_number: '',
    email: '',
    address: '',
    active: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Contact dialog state
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [exportingVendors, setExportingVendors] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    contact_name: '',
    phone_number: '',
    email: '',
    role: ''
  });
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await getVendors();
      setVendors(res.data);
    } catch (error) {
      toast.error("Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async (vendorId) => {
    setLoadingContacts(true);
    try {
      const res = await getVendorContacts(vendorId);
      setContacts(res.data);
    } catch (error) {
      toast.error("Failed to load vendors");
    } finally {
      setLoadingContacts(false);
    }
  };

  const filteredVendors = vendors.filter(v =>
    v.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.phone_number?.includes(searchQuery)
  );

  const openCreateVendorDialog = () => {
    setEditingVendor(null);
    setVendorFormData({
      vendor_name: '',
      phone_number: '',
      email: '',
      address: '',
      active: true
    });
    setError('');
    setVendorDialogOpen(true);
  };

  const openEditVendorDialog = (vendor) => {
    setEditingVendor(vendor);
    setVendorFormData({
      vendor_name: vendor.vendor_name,
      phone_number: vendor.phone_number || '',
      email: vendor.email || '',
      address: vendor.address || '',
      active: vendor.active
    });
    setError('');
    setVendorDialogOpen(true);
  };

  const handleSaveVendor = async () => {
    if (!vendorFormData.vendor_name.trim()) {
      setError('Vendor name is required');
      return;
    }

    setSaving(true);
    setError('');
    
    try {
      if (editingVendor) {
        await updateVendor(editingVendor.id, vendorFormData);
      } else {
        await createVendor(vendorFormData);
      }
      setVendorDialogOpen(false);
      fetchVendors();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVendor = async () => {
    if (!vendorToDelete) return;
    
    setDeleting(true);
    try {
      await deleteVendor(vendorToDelete.id);
      setDeleteDialogOpen(false);
      setVendorToDelete(null);
      fetchVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete vendor');
    } finally {
      setDeleting(false);
    }
  };

  const openContactsDialog = async (vendor) => {
    setSelectedVendor(vendor);
    setContactFormData({ contact_name: '', phone_number: '', email: '', role: '' });
    setContactDialogOpen(true);
    await fetchContacts(vendor.id);
  };

  const handleAddContact = async () => {
    if (!contactFormData.contact_name.trim()) return;
    
    setSavingContact(true);
    try {
      await createVendorContact(selectedVendor.id, contactFormData);
      setContactFormData({ contact_name: '', phone_number: '', email: '', role: '' });
      await fetchContacts(selectedVendor.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add contact');
    } finally {
      setSavingContact(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await deleteVendorContact(contactId);
      await fetchContacts(selectedVendor.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete contact');
    }
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="vendors-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="heading-2 text-[#1F2937]">Vendor Management</h1>
          <p className="text-[#6B7280]">Manage suppliers and their contact information</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button onClick={async () => {
            setExportingVendors(true);
            try {
              const res = await exportVendors();
              downloadBlob(res.data, `vendors_${new Date().toISOString().split('T')[0]}.xlsx`);
            } catch (e) { alert('Export failed.'); } finally { setExportingVendors(false); }
          }} disabled={exportingVendors || vendors.length === 0}
            className="gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg" data-testid="export-vendors-btn">
            {exportingVendors ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={18} />}
            Export Excel
          </Button>
          <Button onClick={() => window.print()} variant="outline"
            className="gap-2 rounded-lg border-[#0776BD] text-[#0776BD]" data-testid="print-vendors-btn">
            <Printer size={18} /> Print
          </Button>
          <Button onClick={openCreateVendorDialog} className="gap-2 bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="add-vendor-btn">
            <Plus size={18} /> Add Vendor
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={20} />
            <Input
              placeholder="Search vendors by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 rounded-lg border-gray-300 focus:border-[#0776BD]"
              data-testid="search-vendors-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Vendors Table */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
            <Building2 size={20} className="text-[#0776BD]" />
            Vendors ({filteredVendors.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
            </div>
          ) : filteredVendors.length === 0 ? (
            <div className="py-12 text-center text-[#6B7280]">
              <Building2 size={48} className="mx-auto mb-4 opacity-50" />
              <p>No vendors found</p>
              <p className="text-sm">Click "Add Vendor" to create one</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5F6F7]">
                  <TableHead className="font-semibold text-[#1F2937]">Vendor Name</TableHead>
                  <TableHead className="font-semibold text-[#1F2937]">Contact Info</TableHead>
                  <TableHead className="font-semibold text-[#1F2937]">Address</TableHead>
                  <TableHead className="font-semibold text-[#1F2937] text-center">Status</TableHead>
                  <TableHead className="font-semibold text-[#1F2937] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVendors.map((vendor) => (
                  <TableRow key={vendor.id} className="hover:bg-[#F5F6F7]" data-testid={`vendor-row-${vendor.id}`}>
                    <TableCell>
                      <p className="font-medium text-[#1F2937]">{vendor.vendor_name}</p>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {vendor.phone_number && (
                          <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                            <Phone size={14} />
                            {vendor.phone_number}
                          </div>
                        )}
                        {vendor.email && (
                          <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                            <Mail size={14} />
                            {vendor.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {vendor.address && (
                        <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                          <MapPin size={14} />
                          <span className="truncate max-w-[200px]">{vendor.address}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={vendor.active ? "bg-green-100 text-green-800 border-0" : "bg-gray-100 text-gray-600 border-0"}>
                        {vendor.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openContactsDialog(vendor)}
                          className="hover:bg-[#E6F2FA] text-[#0776BD]"
                          title="Manage Contacts"
                          data-testid={`contacts-vendor-${vendor.id}`}
                        >
                          <Users size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditVendorDialog(vendor)}
                          className="hover:bg-[#E6F2FA] text-[#0776BD]"
                          data-testid={`edit-vendor-${vendor.id}`}
                        >
                          <Edit size={18} />
                        </Button>
                        {isAdmin() && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setVendorToDelete(vendor); setDeleteDialogOpen(true); }}
                            className="hover:bg-red-50 text-red-500"
                            data-testid={`delete-vendor-${vendor.id}`}
                          >
                            <Trash2 size={18} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Vendor Dialog */}
      <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
        <DialogContent className="max-w-lg rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937]">
              {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
            </DialogTitle>
          </DialogHeader>
          
          {error && (
            <Alert variant="destructive" className="rounded-lg">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Vendor Name *</Label>
              <Input
                value={vendorFormData.vendor_name}
                onChange={(e) => setVendorFormData({ ...vendorFormData, vendor_name: e.target.value })}
                placeholder="e.g., Electrical Supply Co."
                className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD]"
                data-testid="vendor-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937]">Phone Number</Label>
                <Input
                  value={vendorFormData.phone_number}
                  onChange={(e) => setVendorFormData({ ...vendorFormData, phone_number: e.target.value })}
                  placeholder="(702) 555-0100"
                  className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD]"
                  data-testid="vendor-phone-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937]">Email</Label>
                <Input
                  type="email"
                  value={vendorFormData.email}
                  onChange={(e) => setVendorFormData({ ...vendorFormData, email: e.target.value })}
                  placeholder="orders@vendor.com"
                  className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD]"
                  data-testid="vendor-email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Address</Label>
              <Input
                value={vendorFormData.address}
                onChange={(e) => setVendorFormData({ ...vendorFormData, address: e.target.value })}
                placeholder="123 Industrial Blvd, Las Vegas, NV"
                className="h-12 rounded-lg border-gray-300 focus:border-[#0776BD]"
                data-testid="vendor-address-input"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-[#1F2937]">Active Status</Label>
              <Switch
                checked={vendorFormData.active}
                onCheckedChange={(checked) => setVendorFormData({ ...vendorFormData, active: checked })}
                data-testid="vendor-active-switch"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setVendorDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={handleSaveVendor} disabled={saving} className="bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="save-vendor-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingVendor ? 'Update Vendor' : 'Create Vendor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937]">Deactivate Vendor</DialogTitle>
          </DialogHeader>
          <p className="text-[#6B7280]">Are you sure you want to deactivate "{vendorToDelete?.vendor_name}"? This vendor will no longer appear in dropdowns.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteVendor} disabled={deleting} className="rounded-lg" data-testid="confirm-delete-vendor-btn">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contacts Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="max-w-2xl rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937]">
              Contacts - {selectedVendor?.vendor_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add Contact Form */}
            <div className="p-4 bg-[#F5F6F7] rounded-lg">
              <p className="text-sm font-medium text-[#1F2937] mb-3">Add New Contact</p>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  value={contactFormData.contact_name}
                  onChange={(e) => setContactFormData({ ...contactFormData, contact_name: e.target.value })}
                  placeholder="Contact Name *"
                  className="h-10 rounded-lg border-gray-300"
                />
                <Input
                  value={contactFormData.role}
                  onChange={(e) => setContactFormData({ ...contactFormData, role: e.target.value })}
                  placeholder="Role (e.g., Sales Rep)"
                  className="h-10 rounded-lg border-gray-300"
                />
                <Input
                  value={contactFormData.phone_number}
                  onChange={(e) => setContactFormData({ ...contactFormData, phone_number: e.target.value })}
                  placeholder="Phone Number"
                  className="h-10 rounded-lg border-gray-300"
                />
                <Input
                  type="email"
                  value={contactFormData.email}
                  onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                  placeholder="Email"
                  className="h-10 rounded-lg border-gray-300"
                />
              </div>
              <Button 
                onClick={handleAddContact} 
                disabled={savingContact || !contactFormData.contact_name.trim()}
                className="mt-3 bg-[#0776BD] hover:bg-[#055A92] rounded-lg"
              >
                {savingContact ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
                Add Contact
              </Button>
            </div>
            
            {/* Contacts List */}
            {loadingContacts ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[#0776BD]" />
              </div>
            ) : contacts.length === 0 ? (
              <div className="py-8 text-center text-[#6B7280]">
                <Users size={32} className="mx-auto mb-2 opacity-50" />
                <p>No contacts added yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead className="font-semibold text-[#1F2937]">Name</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Role</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Phone</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Email</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow key={contact.id}>
                      <TableCell className="font-medium">{contact.contact_name}</TableCell>
                      <TableCell className="text-[#6B7280]">{contact.role || '-'}</TableCell>
                      <TableCell className="text-[#6B7280]">{contact.phone_number || '-'}</TableCell>
                      <TableCell className="text-[#6B7280]">{contact.email || '-'}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteContact(contact.id)}
                          className="hover:bg-red-50 text-red-500"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDialogOpen(false)} className="rounded-lg">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Vendors;
